import React from 'react';
import './RefreshRatesButton.css';

/**
 * Re-price the lane.
 *
 * An icon rather than a labelled button: it sits beside a heading that already
 * says what the list is, so the word "Refresh" was only restating it. The
 * rotation is the affordance *and* the progress indicator — while it spins the
 * list is being replaced, so there is one signal to read rather than two.
 *
 * Shared by the exception-label and manual-order courier steps so the two
 * cannot drift apart.
 */
function RefreshRatesButton({ onClick, loading, title = 'Refresh rates' }) {
  return (
    <button
      type="button"
      className={`refreshRatesBtn ${loading ? 'isSpinning' : ''}`}
      onClick={onClick}
      disabled={loading}
      // The accessible name changes with state, so a screen reader hears the
      // fetch start rather than only seeing it.
      aria-label={loading ? 'Fetching rates' : title}
      title={loading ? 'Fetching rates…' : title}
    >
      <svg
        viewBox="0 0 24 24"
        width="18"
        height="18"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        <polyline points="21 3 21 9 15 9" />
      </svg>
    </button>
  );
}

/**
 * Placeholder cards shown while rates are being fetched.
 *
 * Lives beside the button because the two are one state: the icon spins, the
 * list is replaced. Exported so the exception-label and manual-order steps
 * render the identical thing rather than each keeping their own copy.
 *
 * `count` is how many placeholders to draw — six by default, which fills a wide
 * grid without implying a specific number of results. The grid itself reflows to
 * the same columns the real cards use, so nothing jumps when they arrive.
 */
export function QuotesSkeleton({ count = 6 }) {
  return (
    <div className="quotesSkeleton" aria-busy="true" aria-live="polite">
      {Array.from({ length: count }, (_, index) => (
        <div className="quotesSkeletonCard" key={index}>
          <div className="quotesSkeletonHead">
            <span className="quotesSkeletonLine" />
            <span className="quotesSkeletonLine" />
          </div>
          <div className="quotesSkeletonBody">
            <span className="quotesSkeletonLine" />
            <span className="quotesSkeletonLine" />
            <span className="quotesSkeletonLine" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default RefreshRatesButton;
